import type { FormKitSchemaNode } from '@formkit/core'
import { generateRandomString } from '@/vf'
import { computed, ref } from 'vue'
import { formElements } from './formElements'
import type { EditorNode, FieldType } from './types'

let _nameCounter = 0

export function useFormBuilder(initialNodes: EditorNode[] = []) {
    const nodes = ref<EditorNode[]>(initialNodes.map(n => ({ ...n })))
    const selectedId = ref<string | null>(null)

    const selectedNode = computed(() =>
        selectedId.value ? findNode(nodes.value, selectedId.value) : null,
    )

    function createNode(type: FieldType): EditorNode {
        _nameCounter++
        return {
            _id: generateRandomString(8),
            type,
            name: type !== 'layout' ? `${type}_${_nameCounter}` : '',
            label: type === 'submit' ? 'Absenden' : '',
            help: '',
            placeholder: '',
            required: false,
            validation: '',
            options: formElements.get(type)!.hasOptions ? [{ label: 'Option 1', value: 'option_1' }] : [],
            columns: 2,
            children: [],
            accept: '',
            maxFileSize: null,
            maxFiles: 1,
            useAsIssueSubtitle: false,
        }
    }

    function nodeToSchema(node: EditorNode): FormKitSchemaNode {
        if (node.type === 'layout') {
            const cols = node.columns || 2
            const colClass = `col-${Math.floor(12 / cols)}`
            return {
                $el: 'div',
                attrs: { class: 'row' },
                children: node.children.map(child => ({
                    $el: 'div',
                    attrs: { class: colClass },
                    children: [nodeToSchema(child)],
                })),
            }
        }

        const formkitType = node.type === 'topic' ? 'select' : node.type
        const schema: Record<string, unknown> = { $formkit: formkitType }
        if (node.name) { schema.name = node.name }
        if (node.label) { schema.label = node.label }
        if (node.help) { schema.help = node.help }
        if (node.placeholder) { schema.placeholder = node.placeholder }
        if (node.type === 'upload') {
            if (node.accept) { schema.accept = node.accept }
            if (node.maxFiles) { schema.maxFiles = node.maxFiles }
        }
        const validation = [
            node.required ? 'required' : '',
            node.type === 'upload' && node.maxFileSize ? `max_file_size:${node.maxFileSize}` : '',
            node.type === 'upload' && node.maxFiles && node.maxFiles > 1 ? `max:${node.maxFiles}` : '',
            node.validation,
        ].filter(Boolean).join('|')
        if (validation) { schema.validation = validation }
        if ((formElements.get(node.type)?.hasOptions || node.type === 'topic') && node.options.length) {
            schema.options = node.options.map(o => ({ label: o.label, value: o.value }))
        }

        return schema as unknown as FormKitSchemaNode
    }

    function findNode(list: EditorNode[], id: string): EditorNode | null {
        for (const node of list) {
            if (node._id === id) { return node }
            const found = findNode(node.children, id)
            if (found) { return found }
        }
        return null
    }

    function spliceNode(list: EditorNode[], id: string): boolean {
        for (let i = 0; i < list.length; i++) {
            if (list[i]._id === id) {
                list.splice(i, 1)
                return true
            }
            if (spliceNode(list[i].children, id)) { return true }
        }
        return false
    }

    function deepClone(node: EditorNode): EditorNode {
        _nameCounter++
        return {
            ...node,
            _id: generateRandomString(8),
            name: node.name ? `${node.type}_${_nameCounter}` : '',
            options: node.options.map(o => ({ ...o })),
            children: node.children.map(deepClone),
        }
    }

    function insertAfter(list: EditorNode[], id: string, clone: EditorNode): boolean {
        for (let i = 0; i < list.length; i++) {
            if (list[i]._id === id) {
                list.splice(i + 1, 0, clone)
                return true
            }
            if (insertAfter(list[i].children, id, clone)) { return true }
        }
        return false
    }

    function selectNode(id: string | null) {
        selectedId.value = id
    }

    function deleteNode(id: string) {
        if (selectedId.value === id) { selectedId.value = null }
        spliceNode(nodes.value, id)
    }

    function duplicateNode(id: string) {
        const node = findNode(nodes.value, id)
        if (!node) { return }
        insertAfter(nodes.value, id, deepClone(node))
    }

    function toSchema(): FormKitSchemaNode[] {
        return nodes.value.map(nodeToSchema)
    }

    return { nodes, selectedId, selectedNode, createNode, selectNode, deleteNode, duplicateNode, toSchema }
}

export type FormBuilder = ReturnType<typeof useFormBuilder>
